export const USE_DEFAULT_MODEL_OPTION_VALUE = '__use-default__'
export const MODEL_SELECT_PRIMARY_LIMIT = 3

export interface ModelSelectDisplayModel {
  id: string
  label: string
}

export interface ModelSelectSearchModel extends ModelSelectDisplayModel {
  providerId: string
}

export interface ModelSelectSearchProvider {
  id: string
  label: string
}

export interface ModelSelectAvailabilityModel {
  requiresMemberIntegration?: string
}

export interface ModelSelectDefaultPresentation {
  triggerLabel: string | undefined
  optionDescription: string
}

/**
 * Keep the inherited trigger compact while preserving the concrete fallback
 * model in the reset action, where users need it to make an informed choice.
 */
export function resolveModelSelectDefaultPresentation(input: {
  defaultModelId: string | undefined
  isUsingDefault: boolean
  models: readonly ModelSelectDisplayModel[]
}): ModelSelectDefaultPresentation {
  const defaultModel = input.models.find(
    (model) => model.id === input.defaultModelId,
  )

  return {
    triggerLabel: input.isUsingDefault ? 'Default' : undefined,
    optionDescription: defaultModel
      ? `${defaultModel.label} fallback`
      : 'Default model fallback',
  }
}

/**
 * Models backed by a member integration only belong in the picker while that
 * integration is active. Models without that requirement remain available.
 */
export function filterAvailableModelSelectModels<
  T extends ModelSelectAvailabilityModel,
>(input: {
  models: readonly T[]
  connectedIntegrationIds: ReadonlySet<string>
}): T[] {
  return input.models.filter(
    (model) =>
      model.requiresMemberIntegration === undefined ||
      input.connectedIntegrationIds.has(model.requiresMemberIntegration),
  )
}

/**
 * Filter the catalog by the terms users can see (model and provider labels) as
 * well as the provider-qualified id they may know from configuration files.
 */
export function filterModelSelectModels<
  T extends ModelSelectSearchModel,
>(input: {
  models: readonly T[]
  providers: readonly ModelSelectSearchProvider[]
  query: string
}): T[] {
  const query = input.query.trim().toLocaleLowerCase()
  if (!query) return [...input.models]

  const providerLabels = new Map(
    input.providers.map((provider) => [provider.id, provider.label]),
  )

  return input.models.filter((model) => {
    const searchableValues = [
      model.label,
      model.id,
      providerLabels.get(model.providerId),
    ]
    return searchableValues.some((value) =>
      value?.toLocaleLowerCase().includes(query),
    )
  })
}

/**
 * Keep each harness group short while preserving the catalog's curated order.
 * Search runs before this split, so the first matching models for every harness
 * remain one click away and only that harness's additional matches move into
 * the More submenu.
 */
export function partitionModelSelectModels<T extends { providerId: string }>(
  models: readonly T[],
  primaryLimit = MODEL_SELECT_PRIMARY_LIMIT,
): { primary: T[]; more: T[] } {
  const countsByProvider = new Map<string, number>()
  const primary: T[] = []
  const more: T[] = []

  for (const model of models) {
    const providerCount = countsByProvider.get(model.providerId) ?? 0
    if (providerCount < primaryLimit) {
      primary.push(model)
    } else {
      more.push(model)
    }
    countsByProvider.set(model.providerId, providerCount + 1)
  }

  return { primary, more }
}
